import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Container from "../../../components/Container";
import { useTranslation } from "react-i18next";

/** 날짜 포맷터 (YYYY-MM-DD HH:mm) */
const fmtDateTime = (d) => {
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${hh}:${mm}`;
};

export default function InquiryEdit() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const fromStatePost = location.state?.post || null;

  // 폼 상태
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [secret, setSecret] = useState(false);
  const [content, setContent] = useState("");
  const [createdAt, setCreatedAt] = useState(null);

  // 검증용 비밀번호 (작성자 전용)
  const [password, setPassword] = useState("");

  // 보조 상태
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // 로그인 사용자 권한(관리자 여부)
  const [amAdmin, setAmAdmin] = useState(false);

  const TITLE_MAX = 120;
  const AUTHOR_MAX = 40;
  const CONTENT_MAX = 5000;

  // 🔹 로그인/관리자 여부 확인 (상세 페이지와 동일)
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch("/api/user/status", {
          credentials: "include", // 쿠키(access_token) 같이 보내기
        });
        if (!res.ok) return;
        const body = await res.json();
        setAmAdmin(body.isAdmin === true);
      } catch (e) {
        console.error(e);
      }
    };
    fetchStatus();
  }, []);

  // 초기 데이터 세팅
  useEffect(() => {
    const applyPost = (p) => {
      setTitle(p.title || "");
      setAuthor(p.author || "");
      setEmail(p.email || "");
      setPhone(p.phone || "");
      setSecret(!!p.secret);
      setContent(p.content || "");
      setCreatedAt(p.createdAt || null);
    };

    (async () => {
      try {
        setLoading(true);
        setError("");

        if (fromStatePost) {
          // 상세 페이지에서 넘겨준 데이터 사용
          applyPost(fromStatePost);
        } else {
          // 새로고침 등으로 state가 없는 경우 다시 조회
          const res = await fetch(`/api/inquiry/detail/${id}`);
          if (!res.ok) throw new Error("HTTP " + res.status);
          const api = await res.json();
          if (!api.data) throw new Error("NO_DATA");
          applyPost(api.data);
        }
      } catch (err) {
        console.error(err);
        setError(
          t("inquiryEdit.err.loadFail", "수정할 글을 불러오지 못했습니다.")
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [id, fromStatePost, t]);

  const validate = () => {
    if (!title.trim())
      return t("inquiryEdit.err.needTitle", "제목을 입력해 주세요.");
    if (!author.trim())
      return t("inquiryEdit.err.needAuthor", "작성자를 입력해 주세요.");
    if (!content.trim())
      return t("inquiryEdit.err.needContent", "내용을 입력해 주세요.");

    const hasEmail = !!email.trim();
    const hasPhone = !!phone.trim();

    // ✅ 이메일 / 연락처 둘 다 필수
    if (!hasEmail || !hasPhone) return t("inquiryWrite.err.needContact");

    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return t(
        "inquiryEdit.err.badEmail",
        "올바른 이메일 형식을 입력해 주세요."
      );
    if (hasPhone && !/^[0-9\-+() ]{7,20}$/.test(phone))
      return t(
        "inquiryEdit.err.badPhone",
        "올바른 연락처 형식을 입력해 주세요."
      );

    // ✅ 일반 사용자는 비밀번호 필수, 관리자는 패스
    if (!amAdmin && !password.trim())
      return t(
        "inquiryEdit.err.needPassword",
        "수정을 위해 비밀번호를 입력해 주세요."
      );

    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setDone(false);

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        title: title.trim(),
        author: author.trim(),
        email: email.trim(),
        phone: phone.trim(),
        secret: secret,
        content: content.trim(),
      };

      // ✅ 일반 사용자만 비밀번호 전송 (작성자 검증용)
      if (!amAdmin) {
        body.password = password.trim();
      }

      const res = await fetch(`/api/inquiry/detail/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error("HTTP " + res.status);

      const api = await res.json();
      if (!api.data) throw new Error("NO_DATA");

      setDone(true);

      // 다시 상세 화면으로 돌아가기 (이전 페이지)
      nav(-1);
    } catch (err) {
      console.error(err);
      setError(
        t(
          "inquiryEdit.err.server",
          "수정 중 오류가 발생했습니다. 비밀번호를 다시 확인한 뒤, 잠시 후 다시 시도해 주세요."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-sm text-slate-500">
        {t("inquiryEdit.loading", "수정할 문의를 불러오는 중입니다...")}
      </div>
    );
  }

  return (
    <div className="text-blue-900">
      {/* 헤더 */}
      <section className="border-b bg-white">
        <Container className="py-10">
          <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">
            {t("inquiryEdit.title", "문의 수정")}
          </h1>
          <p className="mt-2 text-slate-600">
            {t(
              "inquiryEdit.desc",
              "작성하신 문의 내용을 수정할 수 있습니다. 수정 시 비밀번호가 필요합니다."
            )}
          </p>
          {amAdmin && (
            <p className="mt-1 text-xs font-medium text-emerald-700">
              {t(
                "inquiryEdit.adminInfo",
                "관리자 계정으로 접속 중입니다. 비밀번호 없이 수정할 수 있습니다."
              )}
            </p>
          )}
          {createdAt && (
            <p className="mt-1 text-xs text-slate-400">
              {t("inquiryEdit.createdAt", "작성일")} : {fmtDateTime(createdAt)}
            </p>
          )}
        </Container>
      </section>

      {/* 폼 */}
      <section className="py-12">
        <Container>
          <form
            onSubmit={onSubmit}
            className="mx-auto max-w-3xl space-y-6 rounded-2xl border bg-white p-6 shadow-sm"
          >
            {/* 제목 */}
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-slate-800"
              >
                {t("inquiryEdit.f.title", "제목")}{" "}
                <span className="text-red-600">*</span>
              </label>
              <input
                id="title"
                type="text"
                maxLength={TITLE_MAX}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t(
                  "inquiryEdit.ph.title",
                  "수정할 제목을 입력해 주세요."
                )}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="mt-1 text-right text-[11px] text-slate-500">
                {title.length}/{TITLE_MAX}
              </div>
            </div>

            {/* 작성자 + 비밀글 */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="author"
                  className="block text-sm font-medium text-slate-800"
                >
                  {t("inquiryEdit.f.author", "작성자")}{" "}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  id="author"
                  type="text"
                  maxLength={AUTHOR_MAX}
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder={t(
                    "inquiryEdit.ph.author",
                    "작성자 이름을 입력해 주세요."
                  )}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* 비밀글 여부 */}
              <div className="flex items-end">
                <div>
                  <label className="block text-sm font-medium text-slate-800">
                    {t("inquiryEdit.f.secret", "비밀글 설정")}
                  </label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      id="secret"
                      type="checkbox"
                      checked={secret}
                      onChange={(e) => setSecret(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label
                      htmlFor="secret"
                      className="select-none text-sm text-slate-700"
                    >
                      {t(
                        "inquiryEdit.ph.secret",
                        "체크하면 이 글은 비공개로 처리됩니다."
                      )}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* 이메일 / 연락처 */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-slate-800"
                >
                  {t("inquiryEdit.f.email", "이메일")}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t(
                    "inquiryEdit.ph.email",
                    "답변을 받으실 이메일을 입력해 주세요."
                  )}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-slate-800"
                >
                  {t("inquiryEdit.f.phone", "연락처")}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t(
                    "inquiryEdit.ph.phone",
                    "하이픈(-) 포함, 숫자만 입력해도 됩니다."
                  )}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* 내용 */}
            <div>
              <label
                htmlFor="content"
                className="block text-sm font-medium text-slate-800"
              >
                {t("inquiryEdit.f.content", "내용")}{" "}
                <span className="text-red-600">*</span>
              </label>
              <textarea
                id="content"
                rows={12}
                maxLength={CONTENT_MAX}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t(
                  "inquiryEdit.ph.content",
                  "수정할 내용을 입력해 주세요."
                )}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="mt-1 text-right text-[11px] text-slate-500">
                {content.length}/{CONTENT_MAX}
              </div>
            </div>

            {/* 비밀번호 (검증용) – 일반 사용자만 노출 */}
            {!amAdmin && (
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-slate-800"
                >
                  {t("inquiryEdit.f.password", "비밀번호 (수정 권한 확인용)")}{" "}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t(
                    "inquiryEdit.ph.password",
                    "작성 시 사용한 비밀번호를 입력해 주세요."
                  )}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {/* 메시지 */}
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            {done && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                {t("inquiryEdit.done", "문의가 성공적으로 수정되었습니다.")}
              </div>
            )}

            {/* 액션 */}
            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => nav(-1)}
                className="rounded-xl px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
              >
                {t("common.cancel", "취소")}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {submitting
                  ? t("inquiryEdit.actions.submitting", "수정 중...")
                  : t("inquiryEdit.actions.submit", "수정 완료")}
              </button>
            </div>
          </form>
        </Container>
      </section>
    </div>
  );
}
